import { VoxelWorld } from "../core/VoxelWorld.ts";
import { executeBuildProtocol } from "../core/BuildProtocol.ts";
import { BLOCK_DEFINITIONS, getBlockStateOptions, installVersionBlockCatalog } from "../core/blockRegistry.ts";
import { createGenericBlockDefinition } from "../core/MinecraftBlockCatalog.ts";
import { parseBlockSpec, serializeBlockSpec } from "../core/blockSpec.ts";
import type { MinecraftBlockCatalogEntry } from "../core/MinecraftBlockCatalog.ts";

export interface Position { x: number; y: number; z: number }
export interface Bounds { min: Position; max: Position }
export interface DesignRegion { id: string; name: string; purpose: string; bounds: Bounds }
export interface ReviewFinding {
  id: string;
  author: string;
  severity: "info" | "warning" | "error";
  message: string;
  bounds: Bounds;
  status: "open" | "resolved" | "dismissed";
  sceneRevision: string;
  suggestion?: string;
}
export interface ReviewDocument {
  format: "minecraft-voxel-review";
  version: 1;
  sceneRevision: string;
  findings: ReviewFinding[];
}
export interface ProjectDocument {
  format: "minecraft-voxel-studio";
  version: 1;
  name: string;
  brief: string;
  origin: Position;
  coordinateSystem: typeof COORDINATE_SYSTEM;
  sceneRevision: string;
  catalogSource: "official" | "curated";
  catalog: MinecraftBlockCatalogEntry[];
  world: ReturnType<VoxelWorld["toJSON"]>;
  regions: DesignRegion[];
  reviews: ReviewFinding[];
  history: BuildReceipt[];
}
export interface VoxelDelta {
  position: Position;
  before: ReturnType<VoxelWorld["entries"]>[number] | null;
  after: ReturnType<VoxelWorld["entries"]>[number] | null;
}
export interface BuildReceipt {
  author: string;
  baseRevision: string;
  resultRevision: string;
  changedBlockCount: number;
  protocol: unknown;
}
export const COORDINATE_SYSTEM = {
  units: "one Minecraft block",
  axes: { x: "east", y: "up", z: "south" },
  positions: "integer local block coordinates; absent cells are air",
  bounds: "inclusive min and max",
  export: "Minecraft position = local position + project.origin"
} as const;
const MAX_BLOCKS = 500000;
const MAX_ITEMS = 10000;

export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return "[" + value.map(stableStringify).join(",") + "]";
  if (value && typeof value === "object") {
    return "{" + Object.keys(value).sort().filter(key => (value as Record<string, unknown>)[key] !== undefined)
      .map(key => JSON.stringify(key) + ":" + stableStringify((value as Record<string, unknown>)[key])).join(",") + "}";
  }
  return JSON.stringify(value);
}

// A deterministic content fingerprint for stale-edit detection, not a security checksum.
function fingerprint(value: unknown): string {
  let first = 2166136261, second = 2246822519;
  for (const character of stableStringify(value)) {
    const point = character.codePointAt(0)!;
    first = Math.imul(first ^ point, 16777619);
    second = Math.imul(second ^ point, 3266489917);
  }
  return "scene-" + (first >>> 0).toString(16).padStart(8, "0") + (second >>> 0).toString(16).padStart(8, "0");
}

function record(value: unknown, label: string): Record<string, any> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(label + " must be an object.");
  return value as Record<string, any>;
}
function text(value: unknown, label: string, maximum = 10000): string {
  if (typeof value !== "string" || value.length > maximum) throw new Error(label + " must be a string up to " + maximum + " characters.");
  return value;
}
function list(value: unknown, label: string, maximum = MAX_ITEMS): any[] {
  if (!Array.isArray(value) || value.length > maximum) throw new Error(label + " must be an array of at most " + maximum + " items.");
  return value;
}
function clone<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }
export function validatePosition(value: unknown, label = "position"): Position {
  const point = record(value, label);
  for (const axis of ["x", "y", "z"]) {
    if (!Number.isSafeInteger(point[axis]) || Math.abs(point[axis]) > 30000000) throw new Error(label + "." + axis + " must be an integer within ±30,000,000.");
  }
  return { x: point.x, y: point.y, z: point.z };
}
export function validateBounds(value: unknown): Bounds {
  const bounds = record(value, "bounds");
  const min = validatePosition(bounds.min, "bounds.min"), max = validatePosition(bounds.max, "bounds.max");
  for (const axis of ["x", "y", "z"] as const) if (min[axis] > max[axis]) throw new Error("Bounds must have min <= max.");
  return { min, max };
}
export function contains(bounds: Bounds, position: Position): boolean {
  return (["x", "y", "z"] as const).every(axis => position[axis] >= bounds.min[axis] && position[axis] <= bounds.max[axis]);
}
function uniqueIds(items: Array<{id: string}>, label: string) {
  const ids = new Set<string>();
  for (const item of items) {
    if (!item.id || ids.has(item.id)) throw new Error(label + " IDs must be nonempty and unique.");
    ids.add(item.id);
  }
}
function validateRegion(value: unknown): DesignRegion {
  const region = record(value, "region");
  return { id: text(region.id, "region.id", 120), name: text(region.name, "region.name", 200), purpose: text(region.purpose, "region.purpose"), bounds: validateBounds(region.bounds) };
}
function validateFinding(value: unknown, revision: string): ReviewFinding {
  const finding = record(value, "finding");
  if (!["info", "warning", "error"].includes(finding.severity)) throw new Error("Invalid review severity.");
  const status = finding.status ?? "open";
  if (!["open", "resolved", "dismissed"].includes(status)) throw new Error("Invalid review status.");
  if (finding.sceneRevision !== undefined && finding.sceneRevision !== revision) throw new Error("Finding revision must match its review document.");
  return {
    id: text(finding.id, "finding.id", 120), author: text(finding.author, "finding.author", 200),
    severity: finding.severity, message: text(finding.message, "finding.message"), bounds: validateBounds(finding.bounds),
    status, sceneRevision: revision,
    ...(finding.suggestion === undefined ? {} : { suggestion: text(finding.suggestion, "finding.suggestion") })
  };
}
function validateCatalog(value: unknown): MinecraftBlockCatalogEntry[] {
  const catalog = list(value, "catalog");
  const result = catalog.map((entry) => {
    const item = record(entry, "catalog entry");
    if (typeof item.id !== "string" || !/^[a-z0-9_]+$/.test(item.id) || item.id === "air") throw new Error("Invalid catalog block id.");
    const options = record(item.stateOptions, "stateOptions");
    const defaults = record(item.defaultState, "defaultState");
    const stateOptions: Record<string, string[]> = {};
    const defaultState: Record<string, string> = {};
    for (const [key, values] of Object.entries(options)) {
      if (!/^[a-z0-9_]+$/.test(key)) throw new Error("Invalid state property.");
      stateOptions[key] = list(values, "state values", 512).map(value => {
        const result = text(value, "state value", 100);
        if (!/^[a-z0-9_]+$/.test(result)) throw new Error("Invalid state value.");
        return result;
      });
      if (typeof defaults[key] !== "string" || !stateOptions[key].includes(defaults[key])) throw new Error("Default state must be among the advertised state options.");
      defaultState[key] = defaults[key];
    }
    if (Object.keys(defaults).some(key => !(key in stateOptions))) throw new Error("Unexpected default state key.");
    return { id: item.id, stateOptions, defaultState };
  });
  uniqueIds(result.map(item => ({id: item.id})), "Catalog");
  return result;
}

export function currentCatalog(): MinecraftBlockCatalogEntry[] {
  return BLOCK_DEFINITIONS.map(item => ({
    id: item.id, defaultState: clone(item.defaultState ?? {}), stateOptions: clone(getBlockStateOptions(item.id))
  })).sort((a, b) => a.id.localeCompare(b.id));
}

export class AiWorkspace {
  readonly world: VoxelWorld;
  name = "Untitled build";
  brief = "";
  origin: Position = { x: 0, y: 64, z: 0 };
  regions: DesignRegion[] = [];
  reviews: ReviewFinding[] = [];
  history: BuildReceipt[] = [];
  catalogSource: "official" | "curated" = "curated";
  constructor(world = new VoxelWorld()) { this.world = world; }

  private scene() {
    const world = this.world.toJSON();
    world.blocks.sort((a, b) => a.y - b.y || a.z - b.z || a.x - b.x);
    return { name: this.name, brief: this.brief, origin: this.origin, regions: this.regions, catalogSource: this.catalogSource, catalog: currentCatalog(), world };
  }
  get revision(): string { return fingerprint(this.scene()); }
  getProject(): ProjectDocument {
    return clone({ format: "minecraft-voxel-studio", version: 1, ...this.scene(),
      coordinateSystem: COORDINATE_SYSTEM, sceneRevision: this.revision,
      catalogSource: this.catalogSource, catalog: currentCatalog(), reviews: this.reviews, history: this.history });
  }
  setIntent(name: string, brief: string) {
    const checkedName = text(name, "name", 200), checkedBrief = text(brief, "brief");
    this.name = checkedName; this.brief = checkedBrief;
  }
  addRegion(input: unknown) {
    const region = validateRegion(input);
    if (this.regions.some(item => item.id === region.id)) throw new Error("Region ID already exists.");
    if (this.regions.length >= MAX_ITEMS) throw new Error("Too many regions.");
    this.regions.push(region);
    return clone(region);
  }
  importReviews(input: unknown) {
    const document = record(typeof input === "string" ? JSON.parse(input) : input, "review");
    if (document.format !== "minecraft-voxel-review" || document.version !== 1) throw new Error("Expected minecraft-voxel-review version 1.");
    const revision = text(document.sceneRevision, "sceneRevision", 100);
    const findings = list(document.findings, "findings").map(item => validateFinding(item, revision));
    uniqueIds(findings, "Review");
    if (findings.some(finding => this.reviews.some(item => item.id === finding.id))) throw new Error("Review ID already exists; use setReviewStatus to update an existing finding.");
    if (this.reviews.length + findings.length > MAX_ITEMS) throw new Error("Too many review findings.");
    this.reviews.push(...findings);
    return { imported: findings.length, stale: revision !== this.revision };
  }
  setReviewStatus(id: string, status: ReviewFinding["status"]) {
    if (!["open", "resolved", "dismissed"].includes(status)) throw new Error("Invalid review status.");
    const finding = this.reviews.find(item => item.id === id);
    if (!finding) throw new Error("Review finding does not exist.");
    finding.status = status;
    return clone(finding);
  }
  loadProject(input: unknown) {
    if (this.world.activeTransaction) throw new Error("Finish the active edit transaction before importing a project.");
    const data = record(typeof input === "string" ? JSON.parse(input) : input, "project");
    const legacy = data.format === "minecraft-world-designer";
    if (!legacy && (data.format !== "minecraft-voxel-studio" || data.version !== 1)) throw new Error("Unsupported project format/version.");
    if (legacy && ![1, 2, 3, 4].includes(data.version)) throw new Error("Unsupported legacy project version.");
    const worldData = record(legacy ? data : data.world, "world");
    if (!legacy && (worldData.format !== "minecraft-world-designer" || worldData.version !== 4)) throw new Error("Unsupported embedded world format/version.");
    if (worldData.minecraftVersion !== null && worldData.minecraftVersion !== undefined && (typeof worldData.minecraftVersion !== "string" || !/^[a-zA-Z0-9._-]{1,80}$/.test(worldData.minecraftVersion))) throw new Error("Invalid Minecraft version.");
    const blocks = list(worldData.blocks, "blocks", MAX_BLOCKS);
    const seen = new Set<string>();
    const canonicalBlocks = blocks.map(value => {
      const item = record(value, "block"), point = validatePosition(item);
      const key = [point.x, point.y, point.z].join(",");
      if (seen.has(key)) throw new Error("Duplicate block coordinate " + key);
      seen.add(key);
      const spec = text(item.blockSpec ?? item.blockId, "blockSpec", 2000);
      if (!/^(minecraft:)?[a-z0-9_]+(\[[a-z0-9_]+=[a-z0-9_]+(,[a-z0-9_]+=[a-z0-9_]+)*\])?$/.test(spec)) throw new Error("Invalid blockSpec at " + key);
      const parsed = parseBlockSpec(spec, item.state ?? {});
      if (!parsed) throw new Error("Project blocks cannot contain air.");
      if (item.blockId && item.blockId.replace(/^minecraft:/, "") !== parsed.blockId) throw new Error("blockId and blockSpec disagree.");
      if (item.state && item.blockSpec && serializeBlockSpec(spec) !== serializeBlockSpec(spec, item.state)) throw new Error("state and blockSpec disagree.");
      if (item.blockEntity !== undefined && item.blockEntity !== null) record(item.blockEntity, "blockEntity");
      return { ...point, blockSpec: serializeBlockSpec(parsed), blockEntity: item.blockEntity ?? null };
    });
    const catalog = legacy ? null : validateCatalog(data.catalog);
    const name = legacy ? "Imported legacy project" : text(data.name, "name", 200);
    const brief = legacy ? "" : text(data.brief, "brief");
    const origin = validatePosition(data.origin ?? { x: 0, y: 64, z: 0 }, "origin");
    const regions = legacy ? [] : list(data.regions, "regions").map(validateRegion);
    uniqueIds(regions, "Region");
    const reviews = legacy ? [] : list(data.reviews, "reviews").map(item => validateFinding(item, text(item.sceneRevision, "finding.sceneRevision", 100)));
    uniqueIds(reviews, "Review");
    if (!legacy && !["official", "curated"].includes(data.catalogSource)) throw new Error("Invalid catalogSource.");
    const history = legacy ? [] : list(data.history, "history", 100).map(value => {
      const receipt = record(value, "build receipt");
      if (!Number.isSafeInteger(receipt.changedBlockCount) || receipt.changedBlockCount < 0) throw new Error("Invalid changedBlockCount.");
      return { author: text(receipt.author, "author", 200), baseRevision: text(receipt.baseRevision, "baseRevision", 100),
        resultRevision: text(receipt.resultRevision, "resultRevision", 100), changedBlockCount: receipt.changedBlockCount, protocol: clone(receipt.protocol) };
    });
    // Validate every field in a staging world before replacing the active project.
    const staging = new VoxelWorld();
    const previousCatalog = currentCatalog();
    try {
      if (catalog) installVersionBlockCatalog(catalog.map(item => ({
        ...createGenericBlockDefinition(item.id, worldData.minecraftVersion ?? "unverified", {}),
        stateOptions: item.stateOptions, defaultState: item.defaultState
      })));
      staging.loadJSON({ ...worldData, blocks: canonicalBlocks }, {preserveBlockIds:true});
    } catch (error) {
      installVersionBlockCatalog(previousCatalog.map(item => ({
        ...createGenericBlockDefinition(item.id, this.world.minecraftVersion ?? "unverified", {}),
        stateOptions: item.stateOptions, defaultState: item.defaultState
      })));
      throw error;
    }
    this.world.setMinecraftVersion(worldData.minecraftVersion ?? null);
    this.world.loadJSON({ ...worldData, blocks: canonicalBlocks }, {preserveBlockIds:true});
    this.name = name; this.brief = brief; this.origin = origin; this.regions = regions; this.reviews = reviews;
    this.history = history; this.catalogSource = legacy ? "curated" : data.catalogSource;
    this.world.undoStack.length = 0; this.world.redoStack.length = 0;
    return { sceneRevision: this.revision, blockCount: this.world.blocks.size, legacy };
  }

  snapshot(options: { bounds?: Bounds; offset?: number; limit?: number } = {}) {
    const bounds = options.bounds ? validateBounds(options.bounds) : null;
    const offset = options.offset ?? 0, limit = options.limit ?? 200;
    if (!Number.isSafeInteger(offset) || offset < 0 || !Number.isSafeInteger(limit) || limit < 1 || limit > 5000) throw new Error("offset must be nonnegative; limit must be 1…5000.");
    const entries = this.scene().world.blocks.filter(block => !bounds || contains(bounds, block));
    const materials = new Map<string, number>(), layers = new Map<number, number>();
    for (const entry of entries) {
      materials.set(entry.blockSpec, (materials.get(entry.blockSpec) ?? 0) + 1);
      layers.set(entry.y, (layers.get(entry.y) ?? 0) + 1);
    }
    const revision = this.revision;
    return {
      format: "minecraft-voxel-state", version: 1, sceneRevision: revision,
      name: this.name, brief: this.brief, minecraftVersion: this.world.minecraftVersion, origin: clone(this.origin),
      coordinateSystem: COORDINATE_SYSTEM, catalogSource: this.catalogSource,
      totalBlockCount: this.world.blocks.size, matchingBlockCount: entries.length,
      bounds: entries.length ? entries.reduce((result, block) => {
        for (const axis of ["x", "y", "z"] as const) { result.min[axis] = Math.min(result.min[axis], block[axis]); result.max[axis] = Math.max(result.max[axis], block[axis]); }
        return result;
      }, { min: { x: Infinity, y: Infinity, z: Infinity }, max: { x: -Infinity, y: -Infinity, z: -Infinity } }) : null,
      materials: [...materials].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([blockSpec, count]) => ({ blockSpec, count })),
      layers: [...layers].sort((a, b) => a[0] - b[0]).map(([y, blockCount]) => ({ y, blockCount })),
      regions: clone(this.regions), reviews: this.reviews.map(review => ({ ...clone(review), stale: review.sceneRevision !== revision })),
      page: { offset, limit, returned: Math.min(limit, Math.max(0, entries.length - offset)), hasMore: offset + limit < entries.length },
      blocks: entries.slice(offset, offset + limit)
    };
  }
  inspect(position: Position, radius = 1) {
    const point = validatePosition(position);
    if (!Number.isSafeInteger(radius) || radius < 0 || radius > 8) throw new Error("radius must be 0…8.");
    const cells: Array<{position: Position; blockSpec: string | null; blockEntity: unknown}> = [];
    for (let y = point.y - radius; y <= point.y + radius; y++)
      for (let z = point.z - radius; z <= point.z + radius; z++)
        for (let x = point.x - radius; x <= point.x + radius; x++)
          cells.push({ position: { x, y, z }, blockSpec: this.world.getBlockSpec(x, y, z), blockEntity: this.world.getBlockEntityData(x, y, z) });
    return { sceneRevision: this.revision, coordinateSystem: COORDINATE_SYSTEM, center: point, radius, cells,
      regions: clone(this.regions.filter(region => contains(region.bounds, point))) };
  }
  private stage(protocol: unknown, expectedRevision: string) {
    const baseRevision = this.revision;
    if (expectedRevision !== baseRevision) throw new Error("Stale scene revision. Read the current state and preview the edit again.");
    if (this.world.activeTransaction) throw new Error("An editor transaction is still active.");
    const parsed = typeof protocol === "string" ? JSON.parse(protocol) : clone(protocol);
    const staging = new VoxelWorld();
    staging.loadJSON(this.world.toJSON(), {preserveBlockIds:true});
    executeBuildProtocol(staging, parsed);
    if (staging.blocks.size > MAX_BLOCKS) throw new Error("Project exceeds the 500,000 block limit.");
    const before = new Map(this.world.entries().map(block => [[block.x, block.y, block.z].join(","), block]));
    const after = new Map(staging.entries().map(block => [[block.x, block.y, block.z].join(","), block]));
    const changes: VoxelDelta[] = [];
    for (const key of new Set([...before.keys(), ...after.keys()])) {
      const oldBlock = before.get(key) ?? null, newBlock = after.get(key) ?? null;
      if (stableStringify(oldBlock) !== stableStringify(newBlock)) {
        const point = newBlock ?? oldBlock!;
        changes.push({ position: { x: point.x, y: point.y, z: point.z }, before: oldBlock, after: newBlock });
      }
    }
    changes.sort((a, b) => a.position.y - b.position.y || a.position.z - b.position.z || a.position.x - b.position.x);
    return { baseRevision, protocol: parsed, changes };
  }
  preview(protocol: unknown, expectedRevision = this.revision) {
    const staged = this.stage(protocol, expectedRevision);
    return { baseRevision: staged.baseRevision, changedBlockCount: staged.changes.length, changes: staged.changes.slice(0, 200),
      truncated: staged.changes.length > 200, semantics: "Exact block-state diff; does not mutate the project." };
  }
  apply(protocol: unknown, expectedRevision: string, author = "AI builder"): BuildReceipt {
    text(author, "author", 200);
    const staged = this.stage(protocol, expectedRevision);
    this.world.beginTransaction("AI: " + (staged.protocol.name ?? "build"));
    try {
      this.world.runBatch(() => {
        for (const change of staged.changes) {
          const { x, y, z } = change.position;
          this.world.setBlock(x, y, z, change.after?.blockSpec ?? null);
          if (change.after) this.world.setBlockEntityData(x, y, z, change.after.blockEntity);
        }
      });
      this.world.commitTransaction();
    } catch (error) { this.world.cancelTransaction(); throw error; }
    const receipt = { author, baseRevision: staged.baseRevision, resultRevision: this.revision,
      changedBlockCount: staged.changes.length, protocol: staged.protocol };
    this.history.push(clone(receipt)); this.history = this.history.slice(-100);
    return clone(receipt);
  }
  analyze() {
    const state = this.snapshot({limit: 1});
    const entries = this.world.entries(), catalog = new Map(currentCatalog().map(item => [item.id, item]));
    const candidates: Array<{rule: string; message: string; position: Position; blockSpec: string; evidence: unknown}> = [];
    const directions = [[1,0,0],[-1,0,0],[0,1,0],[0,-1,0],[0,0,1],[0,0,-1]];
    let candidateCount = 0;
    function candidate(value: typeof candidates[number]) { candidateCount++; if (candidates.length < 200) candidates.push(value); }
    for (const block of entries) {
      const position = {x:block.x,y:block.y,z:block.z};
      const definition = catalog.get(block.blockId);
      if (!definition) candidate({rule:"unavailable-block", position, blockSpec:block.blockSpec,
        message:"Block is absent from the saved/current catalog; verify target-version availability.", evidence:{catalogSource:this.catalogSource}});
      else for (const [key, value] of Object.entries(block.state)) {
        if (!definition.stateOptions[key]?.includes(value)) candidate({rule:"invalid-state", position, blockSpec:block.blockSpec,
          message:"State is absent from the advertised catalog options.", evidence:{property:key,value,allowed:definition.stateOptions[key] ?? []}});
      }
      const neighbors = directions.map(([dx,dy,dz]) => this.world.getBlock(block.x+dx,block.y+dy,block.z+dz));
      if (neighbors.every(value => value === null)) candidate({rule:"isolated-block",position,blockSpec:block.blockSpec,
        message:"No face-adjacent blocks. Review whether this isolated placement is intentional.",evidence:{occupiedFaceNeighbors:0}});
      const occupied = neighbors.filter(Boolean);
      if (occupied.length >= 4 && !occupied.includes(block.blockId)) candidate({rule:"local-material-outlier",position,blockSpec:block.blockSpec,
        message:"Material differs from all occupied face neighbors. It may be intentional decoration; inspect the design brief and images.",evidence:{neighbors:occupied}});
    }
    const {blocks: _blocks, page: _page, ...summary} = state;
    return {...summary,format:"minecraft-voxel-analysis",version:1, candidateCount, candidates, truncated:candidateCount>candidates.length,
      limitations:["Candidates are geometric/material observations, not aesthetic verdicts.","Curated catalogs do not establish target-version availability.","Inspect images and named design regions before deciding whether a placement is awkward."]};
  }
}
